import "./ordernow.scss";

import { ButtonIconPosition, ButtonVariant } from "@amzn/stencil-react-components/button";
import { withTooltip } from "@amzn/stencil-react-components/dist/submodules/tooltip";
import { Col, Row, View } from "@amzn/stencil-react-components/layout";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import { IconInformationSmall } from "@amzn/stencil-react-icons";
import IconExternalLink from "@amzn/stencil-react-icons/icons/icon-external-link";
import IconQuestionCircle from "@amzn/stencil-react-icons/icons/icon-question-circle";
import Button from "@amzn/uxm-stencil-react-components/lib/src/stencil/button";
import Link from "@amzn/uxm-stencil-react-components/lib/src/stencil/link";
import { ItemOrderWorkflow } from "common/types/catalog-types";
import { RuleProperties } from "json-rules-engine";
import { RulesEngineFactory } from "lib/helpers/rules-engine-factory";
import { AmazonianInformation } from "lib/models/oui-models";
import { UserInformation } from "lib/services/user-information-service";
import { ItemTypes } from "lib/types/items";
import { IItemVersion } from "lib/types/items";
import React, { ReactElement, useEffect, useMemo, useState } from "react";
import { FunctionComponent } from "react";

import { AcmeIntegration } from "../../types/catalog-types";
import { ItemStatus } from "../../types/catalogical-type";
import { getString } from "../../uistringlabels/uiStringUtils";
import { ParentChildActions } from "../ParentChildActions";
import { PriceText } from "../PriceText";
import { VersionObject } from "../SelectChildSku";
import { VendorCostDisclaimer } from "../VendorCostDisclaimer";
import { getOrderButtonText } from "./helpers";

const EXTERNAL_ORDER_WORKFLOW = "external-order";
const MAX_WIDTH_PX = "250px";
const REQUEST_ONBOARDING_URL = "https://t.corp.amazon.com/create/templates/f5a77efa-2286-45f2-bba7-211ed337efe0";
const USER_ALLOW_ORDER = "allow-order";

const buildLaunchButton = (
  productDownloadUrl: string | undefined,
  launchLicensedProduct: () => void,
  isLoading: boolean,
  getString: (key: string) => string
) => {
  if (!productDownloadUrl?.trim()) {
    return null;
  }

  return (
    <Button
      variant={ButtonVariant.Primary}
      icon={<IconExternalLink aria-hidden={true} />}
      iconPosition={ButtonIconPosition.Trailing}
      onClick={launchLicensedProduct}
      id="launchButton"
      touchpoint="launch-button"
      data-cy="launch-button"
      disabled={isLoading}
    >
      {getString("ordering.launch") || "Launch"}
    </Button>
  );
};

const renderLegacyLicensedLayout = (
  amount: number | undefined,
  currency: string | undefined,
  isLegacy: boolean,
  selectLicenseComponent: ReactElement | undefined,
  selectVersionComponent: ReactElement | undefined,
  productDownloadUrl: string | undefined,
  returnButtonComponent: ReactElement | undefined,
  launchButton: ReactElement | null
): ReactElement => (
  <View className="order-now">
    <Row>
      <Col alignItems="end">
        <PriceText showIcon={isLegacy} amount={amount} currency={currency} data-cy="item-price" />
        {selectLicenseComponent && <View style={{ marginTop: "12px", width: "100%" }}>{selectLicenseComponent}</View>}
        {selectVersionComponent && <View style={{ marginTop: "12px", width: "100%" }}>{selectVersionComponent}</View>}

        {/* Launch button first */}
        {Boolean(productDownloadUrl?.trim()) && <Row style={{ marginTop: "16px" }}>{launchButton}</Row>}

        {/* Return button below */}
        <Row style={{ marginTop: "12px" }}>{returnButtonComponent}</Row>
      </Col>
    </Row>
  </View>
);

const renderNewLicensedLayout = (
  amount: number | undefined,
  currency: string | undefined,
  isLegacy: boolean,
  selectLicenseComponent: ReactElement | undefined,
  selectVersionComponent: ReactElement | undefined,
  productDownloadUrl: string | undefined,
  returnButtonComponent: ReactElement | undefined,
  launchButton: ReactElement | null
): ReactElement => (
  <View className="order-now" role="region" aria-label="Product ordering options">
    <Row>
      <Col alignItems="baseline" className="order-now__top-row">
        <PriceText showIcon={isLegacy} amount={amount} currency={currency} data-cy="item-price" />
        {selectLicenseComponent && <View style={{ marginTop: "12px", width: "100%" }}>{selectLicenseComponent}</View>}
        {selectVersionComponent && <View style={{ marginTop: "12px", width: "100%" }}>{selectVersionComponent}</View>}

        {/* Return and Launch in same row */}
        <Row columnGap={"S200"} style={{ marginTop: "16px" }} role="group" aria-label="Product actions">
          {returnButtonComponent}
          {Boolean(productDownloadUrl?.trim()) && launchButton}
        </Row>
      </Col>
    </Row>
  </View>
);

export interface OrderNowProps {
  externalButtonText?: string;
  orderWorkflow: string | undefined;
  orderRedirectUrl: string | undefined;
  moreDetailsUrl: string | undefined;
  status: string | undefined;
  amount: number | undefined;
  currency: string | undefined;
  orderNowHandler: () => void;
  itemType?: ItemTypes | undefined;
  goToCheckoutHandler?: () => void;
  hasReachedCap?: boolean;
  isLoading?: boolean;
  activeOrder?: boolean;
  acme?: AcmeIntegration;
  userOS?: string[];
  isReturnable?: boolean;
  hasLicense?: boolean;
  isLegacy?: boolean;
  selectVersionComponent?: ReactElement;
  selectLicenseComponent?: ReactElement;
  // Parent-Child product props
  isParentChildProduct?: boolean;
  availableTeams?: IItemVersion[];
  onOrderTeam?: (team: VersionObject) => void;
  canReturn?: boolean;
  returnButtonComponent?: ReactElement;
  parentItemName?: string;
  productDownloadUrl?: string;
  isL10ThresholdMeet?: boolean;
  wikiUrl?: string;
  itemRules: RuleProperties[];
}

export const OrderNow: FunctionComponent<OrderNowProps> = ({
  orderNowHandler,
  goToCheckoutHandler,
  orderRedirectUrl,
  moreDetailsUrl,
  status,
  amount,
  currency,
  itemType,
  orderWorkflow,
  hasReachedCap = false,
  isLoading = false,
  externalButtonText,
  activeOrder = false,
  acme,
  userOS = [],
  isReturnable = false,
  hasLicense = false,
  isLegacy = true,
  selectVersionComponent,
  selectLicenseComponent,
  // Parent-Child product props
  isParentChildProduct = false,
  availableTeams = [],
  onOrderTeam,
  returnButtonComponent,
  parentItemName,
  productDownloadUrl,
  isL10ThresholdMeet = false,
  wikiUrl = "",
  itemRules,
}: OrderNowProps) => {
  const IconWithTooltip = withTooltip()(IconQuestionCircle);
  const statusLowerCase = status?.toLowerCase();

  // We know for sure the AmazonianInformation was obtained when AuthLoading component was executed
  const userInfo = UserInformation.current.getCachedAmazonianInformation() as AmazonianInformation;

  // Filter to only "allow-order" rules - these determine if the user can order
  const allowOrderRules = useMemo(() => itemRules.filter((rule) => rule.name === USER_ALLOW_ORDER), [itemRules]);

  // State to store the rules engine result
  // Default to false when "allow-order" rules exist (show "Learn More" until rules are evaluated)
  // Default to true when no "allow-order" rules exist (show "Order" button normally)
  const [allowOrder, setAllowOrder] = useState<boolean>(allowOrderRules.length === 0);
  const [isEvaluatingRules, setIsEvaluatingRules] = useState<boolean>(allowOrderRules.length > 0);

  // Run the rules engine when allowOrderRules or userInfo changes
  useEffect(() => {
    let isMounted = true;

    const evaluateRules = async () => {
      setIsEvaluatingRules(true);
      try {
        const engine = RulesEngineFactory.getRulesEngine(allowOrderRules);
        const facts: Record<string, any> = { userInfo };
        const result = await engine.run(facts);
        if (isMounted) {
          setAllowOrder(result.events.length > 0);
        }
      } catch (error) {
        console.error("Failed to evaluate rules:", error);
        if (isMounted) {
          setAllowOrder(false); // Default to "Learn More" button
        }
      }
      if (isMounted) {
        setIsEvaluatingRules(false);
      }
    };

    if (allowOrderRules.length > 0) {
      evaluateRules();
    } else {
      // No "allow-order" rules means order is allowed by default
      setAllowOrder(true);
      setIsEvaluatingRules(false);
    }

    return () => {
      isMounted = false;
    };
  }, [allowOrderRules, userInfo]);

  const openExternalTab = (url: string) => {
    window.open(url, "_blank");
  };

  const goToOrderRedirect = () => {
    if (orderRedirectUrl) openExternalTab(orderRedirectUrl);
  };

  const launchLicensedProduct = () => {
    if (productDownloadUrl?.trim()) {
      openExternalTab(productDownloadUrl);
    }
  };

  const goToRequestOnboarding = () => {
    openExternalTab(REQUEST_ONBOARDING_URL);
  };

  const requestSecurityReviewHandler = () => {
    if (moreDetailsUrl) openExternalTab(moreDetailsUrl);
  };

  const goToWikiUrl = () => {
    if (wikiUrl) openExternalTab(wikiUrl);
  };

  const getAlreadyReviewedClickHandler = () => {
    if (isLoading) {
      return undefined;
    }

    if (orderWorkflow === ItemOrderWorkflow.NON_ORDERABLE_WORKFLOW) {
      return goToRequestOnboarding;
    }

    return orderNowHandler;
  };

  // Helper function to determine if product is ACME enabled
  const isAcmeEnabled = () => {
    return acme?.isEnabled;
  };

  // Helper function to get the appropriate deep link URL for user's OS
  const getDeepLinkForUserOS = () => {
    if (!acme || !userOS.length) return null;

    // Check if user is on Windows
    if (userOS.includes("Windows")) {
      return acme.windowsDeepLinkUrl;
    }

    // Check if user is on Mac (any Mac variant)
    if (userOS.some((os) => ["MAC", "MacOS", "Mac"].includes(os))) {
      return acme.macOsDeepLinkUrl;
    }

    return null;
  };

  // Helper function to handle Install button click
  const handleInstallClick = () => {
    const deepLinkUrl = getDeepLinkForUserOS();
    if (deepLinkUrl) {
      window.location.href = deepLinkUrl;
    }
  };

  // Helper function to check if Install button should be disabled
  const isInstallButtonDisabled = () => {
    const deepLinkUrl = getDeepLinkForUserOS();
    return !deepLinkUrl || isLoading;
  };

  const renderCostCenterDisclaimer = (amount: number | undefined): React.JSX.Element | undefined => {
    if (!amount || amount === 0) return undefined; // should not display as it will be displaying in PriceText

    return (
      <Row display="flex" alignItems="center" gridGap="S200">
        <IconInformationSmall />
        <Text color="neutral90" fontWeight={"medium"} fontSize={"T200"}>
          {getString("ordering.costDisclaimerText")}
        </Text>
      </Row>
    );
  };

  const renderNoCostMessage = (amount: number | undefined): React.JSX.Element | undefined => {
    return amount != 0 ? undefined : (
      <Row display="flex" alignItems="center" gridGap="S200">
        <IconInformationSmall />
        <Text>{getString("ordering.noCostText")}</Text>
      </Row>
    );
  };

  const renderLearnMoreTooltip = (amount: number | undefined): React.JSX.Element | undefined => {
    return amount == 0 ? undefined : (
      <IconWithTooltip
        title="Show tooltip"
        tooltipText={getString("ordering.learnMoreTooltipText")}
        color="primary70"
        tooltipDataTestId="sanctioned-item-tool-tip"
      />
    );
  };

  const renderAcmeTooltip = () => {
    return (
      <IconWithTooltip
        title="Show tooltip"
        tooltipText={getString("ordering.installTooltipText")}
        color="primary70"
        tooltipDataTestId="acme-item-tool-tip"
      />
    );
  };

  // Create launch button using extracted function
  const launchButton = buildLaunchButton(productDownloadUrl, launchLicensedProduct, isLoading, getString);

  // Main helper function that decides which layout to use
  const renderLicensedProductLayout = (): ReactElement => {
    return isLegacy
      ? renderLegacyLicensedLayout(
          amount,
          currency,
          isLegacy,
          selectLicenseComponent,
          selectVersionComponent,
          productDownloadUrl,
          returnButtonComponent,
          launchButton
        )
      : renderNewLicensedLayout(
          amount,
          currency,
          isLegacy,
          selectLicenseComponent,
          selectVersionComponent,
          productDownloadUrl,
          returnButtonComponent,
          launchButton
        );
  };

  // first thing to check - if the status is unavailable there's no need to evaluate each other possible case
  if (statusLowerCase == ItemStatus.unavailable) {
    return (
      <View className="order-now">
        <Text data-cy="unavailable-title">{getString("ordering.unavailable")}</Text>
      </View>
    );
  }

  // Show spinner while rules are being evaluated
  if (isEvaluatingRules) {
    return (
      <View className="order-now" data-cy="rules-evaluating-spinner">
        <Row>
          <Col alignItems="center">
            <Spinner size={SpinnerSize.Small} />
          </Col>
        </Row>
      </View>
    );
  }

  // For Parent-Child products, use the specialized ParentChildActions component
  if (
    isParentChildProduct &&
    itemType !== ItemTypes.HARDWARE &&
    onOrderTeam &&
    availableTeams &&
    availableTeams.length > 0 &&
    hasLicense
  ) {
    console.log("parent-child with licenses");
    return (
      <ParentChildActions
        productName={parentItemName || "Product"}
        availableTeams={availableTeams}
        hasLicense={hasLicense}
        productDownloadUrl={productDownloadUrl}
        onOrderTeam={onOrderTeam}
        onOrderModalOpen={orderNowHandler}
        isLoading={isLoading}
        returnButtonComponent={returnButtonComponent}
        isLegacy={isLegacy}
      />
    );
  }

  if (itemType === ItemTypes.HARDWARE) {
    const orderButtonText = getString("ordering.orderNow");
    const element = isLegacy ? (
      <View className="order-now">
        <Row>
          <Col alignItems="end">
            <PriceText showIcon={false} amount={amount} currency={currency} isApproximate />
            {!!amount && <VendorCostDisclaimer />}
            <Button
              variant={ButtonVariant.Primary}
              onClick={goToCheckoutHandler}
              data-cy="order-now-button"
              touchpoint="hardware-order-now-button"
              disabled={isLoading}
              icon={isLoading ? <Spinner size={SpinnerSize.Small} /> : undefined}
              iconPosition={ButtonIconPosition.Leading}
            >
              {isLoading ? getString("ordering.validating") : orderButtonText}
            </Button>
          </Col>
        </Row>
      </View>
    ) : (
      <View className="order-now">
        <Row>
          <Col alignItems="start">
            <PriceText showIcon={false} amount={amount} currency={currency} isApproximate />
            {!!amount && <VendorCostDisclaimer />}
            <View>
              {selectVersionComponent}
              {selectLicenseComponent}
            </View>
            <Button
              variant={ButtonVariant.Primary}
              onClick={goToCheckoutHandler}
              data-cy="order-now-button"
              touchpoint="hardware-order-now-button"
              disabled={isLoading}
              icon={isLoading ? <Spinner size={SpinnerSize.Small} /> : undefined}
              iconPosition={ButtonIconPosition.Leading}
              style={{ marginTop: "8px" }}
            >
              {isLoading ? getString("ordering.validating") : orderButtonText}
            </Button>
          </Col>
        </Row>
      </View>
    );
    return element;
  }

  // Check if this is an ACME enabled product that should show Install button
  if (isAcmeEnabled()) {
    return (
      <View className="order-now">
        <Row>
          <Col alignItems="start">
            <PriceText showIcon={isLegacy} amount={amount} currency={currency} data-cy="item-price" />
            <Row columnGap={"S200"}>
              {renderAcmeTooltip()}
              <Button
                variant={ButtonVariant.Secondary}
                onClick={handleInstallClick}
                id="installButton"
                touchpoint="install-button"
                data-cy="install-button"
                disabled={isInstallButtonDisabled()}
                icon={isLoading ? <Spinner size={SpinnerSize.Small} /> : <IconExternalLink aria-hidden={true} />}
                iconPosition={ButtonIconPosition.Trailing}
              >
                {getString("ordering.install")}
              </Button>
            </Row>
          </Col>
        </Row>
      </View>
    );
  }

  // Case 2: Product is returnable and user has a license - use helper function for layout
  if (isReturnable && hasLicense) {
    return renderLicensedProductLayout();
  }

  if (
    statusLowerCase == ItemStatus.sanctioned &&
    orderRedirectUrl != "" &&
    orderRedirectUrl != undefined &&
    orderWorkflow != EXTERNAL_ORDER_WORKFLOW &&
    (allowOrderRules.length === 0 || !allowOrder)
  ) {
    // Three cases for products with orderRedirectURL:
    // Case 1: Product is not returnable - show "Learn More" button (existing behavior)
    return isLegacy ? (
      <View className="order-now">
        <Row>
          <Col alignItems="end">
            <PriceText showIcon={isLegacy} amount={amount} currency={currency} data-cy="item-price" />
            <Row columnGap={"S200"}>
              <IconWithTooltip
                title="Show tooltip"
                tooltipText={getString("ordering.learnMoreTooltipText")}
                color="primary70"
                tooltipDataTestId="sanctioned-item-tool-tip"
              />
              <Button
                icon={<IconExternalLink aria-hidden={true} />}
                iconPosition={ButtonIconPosition.Trailing}
                onClick={goToOrderRedirect}
                id="orderNowLink"
                touchpoint="learn-more-button"
                data-cy="learn-more-button"
                disabled={isLoading}
              >
                {orderWorkflow?.startsWith("external") && externalButtonText
                  ? externalButtonText
                  : getString("ordering.learnMore")}
              </Button>
            </Row>
          </Col>
        </Row>
      </View>
    ) : (
      <View className="order-now">
        <Row>
          <Col alignItems="start">
            <Row columnGap={"S200"} alignItems="center">
              <PriceText showIcon={isLegacy} amount={amount} currency={currency} data-cy="item-price" />
              {renderLearnMoreTooltip(amount)}
            </Row>
            <Row columnGap={"S200"} alignItems="start" style={{ marginTop: "8px" }}>
              {renderCostCenterDisclaimer(amount)}
            </Row>
            <Row columnGap={"S200"} alignItems="start" style={{ marginTop: "8px" }}>
              {renderNoCostMessage(amount)}
            </Row>
            {selectLicenseComponent && (
              <View style={{ marginTop: "12px", width: "100%" }}>{selectLicenseComponent}</View>
            )}
            {selectVersionComponent && (
              <View style={{ marginTop: "12px", width: "100%" }}>{selectVersionComponent}</View>
            )}
            <Button
              icon={<IconExternalLink aria-hidden={true} />}
              iconPosition={ButtonIconPosition.Trailing}
              onClick={goToOrderRedirect}
              id="orderNowLink"
              touchpoint="learn-more-button"
              data-cy="learn-more-button"
              disabled={isLoading}
              style={{ marginTop: "8px" }}
            >
              {orderWorkflow?.startsWith("external") && externalButtonText
                ? externalButtonText
                : getString("ordering.learnMore")}
            </Button>
          </Col>
        </Row>
      </View>
    );
  }

  if (!hasLicense && statusLowerCase == ItemStatus.sanctioned && allowOrder) {
    const orderButtonText = isL10ThresholdMeet ? getString("ordering.learnMore") : getString("ordering.orderNow");
    const iconWhenNotLoading = isL10ThresholdMeet ? <IconExternalLink></IconExternalLink> : <></>;
    const iconProps = {
      icon: isLoading ? <Spinner size={SpinnerSize.Small} /> : iconWhenNotLoading,
      iconPosition: isL10ThresholdMeet ? ButtonIconPosition.Trailing : ButtonIconPosition.Leading,
    };
    const element = isLegacy ? (
      <View className="order-now">
        <Row>
          <Col alignItems="end">
            <PriceText showIcon={isLegacy} amount={amount} currency={currency} />
            <Row columnGap={"S200"} alignItems="center">
              {activeOrder && (
                <IconWithTooltip
                  title="Show tooltip"
                  tooltipText={
                    <Text
                      style={{ whiteSpace: "pre-wrap", textAlign: "left" }}
                      fontSize={"T100"}
                      fontWeight={400}
                      color="neutral90"
                    >
                      {getString("ordering.openOrderTooltipText")}
                    </Text>
                  }
                  color="primary70"
                  tooltipDataTestId="open-order-item-tool-tip"
                />
              )}
              <Button
                variant={ButtonVariant.Primary}
                onClick={isL10ThresholdMeet ? goToWikiUrl : orderNowHandler}
                id="orderNowLink"
                data-cy="order-now-button"
                touchpoint="software-order-now-button"
                disabled={(hasReachedCap && !isL10ThresholdMeet) || isLoading || activeOrder}
                {...iconProps}
              >
                {getOrderButtonText({ isLoading, activeOrder, orderButtonText, getString })}
              </Button>
            </Row>
          </Col>
        </Row>
      </View>
    ) : (
      <View className="order-now" width={"100%"}>
        <Col alignItems="start">
          <PriceText
            showIcon={!!(selectVersionComponent || selectLicenseComponent)}
            amount={amount}
            currency={currency}
          />
          {!(selectVersionComponent || selectLicenseComponent) && renderCostCenterDisclaimer(amount)}
          {amount == 0 && (
            <Row columnGap={"S200"} alignItems="start" style={{ marginTop: "12px" }}>
              <IconWithTooltip
                title="Show tooltip"
                tooltipText={getString("ordering.learnMoreTooltipText")}
                color="primary70"
                tooltipDataTestId="free-item-tool-tip"
              />
              {getString("ordering.noCostText")}
            </Row>
          )}
          <View style={{ marginTop: "12px", width: "100%" }}>{selectLicenseComponent}</View>
          <View style={{ marginTop: "12px", width: "100%" }}>{selectVersionComponent}</View>
          <Row columnGap={"S200"}>
            {activeOrder && (
              <IconWithTooltip
                title="Show tooltip"
                tooltipText={
                  <Text
                    style={{ whiteSpace: "pre-wrap", textAlign: "left" }}
                    fontSize={"T100"}
                    fontWeight={400}
                    color="neutral90"
                  >
                    {getString("ordering.openOrderTooltipText")}
                  </Text>
                }
                color="primary70"
                tooltipDataTestId="open-order-item-tool-tip"
              />
            )}
            <Button
              style={{ marginTop: "16px" }}
              variant={ButtonVariant.Primary}
              onClick={isL10ThresholdMeet ? goToWikiUrl : orderNowHandler}
              id="orderNowLink"
              data-cy="order-now-button"
              touchpoint="software-order-now-button"
              disabled={(hasReachedCap && !isL10ThresholdMeet) || isLoading || activeOrder}
              {...iconProps}
            >
              {getOrderButtonText({ isLoading, activeOrder, orderButtonText, getString })}
            </Button>
          </Row>
        </Col>
      </View>
    );
    return element;
  }

  if (statusLowerCase == ItemStatus.unsanctioned) {
    const element = isLegacy ? (
      <View className="order-now">
        <Row>
          <Col alignItems="end">
            {renderCostCenterDisclaimer(amount)}
            <PriceText showIcon={isLegacy} amount={amount} currency={currency} />
            <Row columnGap={"S200"}>
              <IconWithTooltip
                title="Show tooltip"
                tooltipText={getString("ordering.unsanctionedTooltipText")}
                color="primary70"
                tooltipDataTestId="unsanctioned-item-tool-tip"
              />
              <Button
                icon={<IconExternalLink aria-hidden={true} />}
                iconPosition={ButtonIconPosition.Trailing}
                onClick={requestSecurityReviewHandler}
                disabled={isLoading}
                touchpoint="request-review-button"
                data-cy="request-review-button"
              >
                {getString("ordering.requestSecurityReview")}
              </Button>
            </Row>
            <Row maxWidth={MAX_WIDTH_PX}>
              <Text fontSize="T100" data-cy="already-reviewed-text" className="order-now-text">
                {getString("ordering.unsanctionedMessage")} &nbsp;
                <Link
                  href="#"
                  onClick={getAlreadyReviewedClickHandler()}
                  data-cy="confirm-use-case-link"
                  touchpoint="confirm-use-case-link"
                  style={{ pointerEvents: isLoading ? "none" : "auto" }}
                >
                  {orderWorkflow !== ItemOrderWorkflow.NON_ORDERABLE_WORKFLOW
                    ? getString("ordering.confirmUseCase")
                    : getString("ordering.requestOnboarding")}
                </Link>
              </Text>
            </Row>
          </Col>
        </Row>
      </View>
    ) : (
      <View className="order-now">
        <Row>
          <Col alignItems="start">
            <PriceText
              showIcon={!!(selectVersionComponent || selectLicenseComponent)} // if either one is truthy, we want to show the tooltip
              amount={amount}
              currency={currency}
            />
            {selectLicenseComponent}
            {selectVersionComponent}
            <Row columnGap={"S200"}>
              <IconWithTooltip
                title="Show tooltip"
                tooltipText={getString("ordering.unsanctionedTooltipText")}
                color="primary70"
                tooltipDataTestId="unsanctioned-item-tool-tip"
              />
              <Button
                icon={<IconExternalLink aria-hidden={true} />}
                iconPosition={ButtonIconPosition.Trailing}
                onClick={requestSecurityReviewHandler}
                disabled={isLoading}
                touchpoint="request-review-button"
                data-cy="request-review-button"
              >
                {getString("ordering.requestSecurityReview")}
              </Button>
            </Row>
            <Row maxWidth={MAX_WIDTH_PX}>
              <Text fontSize="T100" data-cy="already-reviewed-text" className="order-now-text">
                {getString("ordering.unsanctionedMessage")} &nbsp;
                <Link
                  href="#"
                  onClick={getAlreadyReviewedClickHandler()}
                  data-cy="confirm-use-case-link"
                  touchpoint="confirm-use-case-link"
                  style={{ pointerEvents: isLoading ? "none" : "auto" }}
                >
                  {orderWorkflow !== ItemOrderWorkflow.NON_ORDERABLE_WORKFLOW
                    ? getString("ordering.confirmUseCase")
                    : getString("ordering.requestOnboarding")}
                </Link>
              </Text>
            </Row>
          </Col>
        </Row>
      </View>
    );
    return element;
  }

  return <div></div>;
};
